import { buildMisoNotificationsByHour, misoDaysForWindow } from '@/helpers/eos/miso-notifications'
import { getMisoNotificationsForSite } from '@/services/eos/miso-notifications.service'
import { useQuery } from '@tanstack/vue-query'
import type { DateTime } from 'luxon'
import { computed, type Ref } from 'vue'

export const useMisoNotifications = () => {
  /**
   * Factory function to define a query for the MISO notifications affecting a site
   * during a 24-hour dashboard window.
   *
   * The endpoint serves one MISO (EST) day per call while the window starts at midnight
   * in the selected timezone, so we fetch every EST day the window touches and transform
   * the responses into the alerts active per hour.
   *
   * @param {Ref<string | undefined>} siteId the selected site ID.
   * @param {Ref<DateTime | undefined>} startHour the window's first hour.
   * @returns a query resolving to a `MisoNotificationsByHour` map.
   */
  const misoNotificationsQuery = (siteId: Ref<string | undefined>, startHour: Ref<DateTime | undefined>) => {
    const days = computed(() => misoDaysForWindow(startHour.value))

    return useQuery({
      queryKey: ['eos', 'miso-notifications', siteId, days],
      queryFn: () => Promise.all(days.value.map((day) => getMisoNotificationsForSite({ site: siteId.value!, day }))),
      select: buildMisoNotificationsByHour,
      enabled: computed(() => !!siteId.value && days.value.length > 0),
      placeholderData: (prev) => prev,
    })
  }

  return { misoNotificationsQuery }
}
